import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
// @mui/x-data-grid state hooks (community API)
import {
  useGridApiContext,
  useGridSelector,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
} from "@mui/x-data-grid";
// @mui/material (v7 — same generation as the DataGrid, so the panel matches it)
import {
  Box,
  Checkbox,
  InputAdornment,
  TextField,
  Typography,
  checkboxClasses,
  outlinedInputClasses,
  svgIconClasses,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";

// Columns-panel replacement (DataGrid `columnsManagement` slot) that presents
// the columns GROUPED by their column group: a show/hide checkbox per group
// (indeterminate when mixed) with the group's columns indented below it. This
// replaces the extra column-group header row the grids used to render — the
// grouping lives here, in the selector, instead of on the table.
//
// Styling mirrors the app's column-visibility popover standard
// (ComparativeTable/ColumnVisibilityControls): group headers as clientColor
// tinted chips, 11px labels in grayColor tones, compact checkboxes that turn
// clientColor when checked.
export default function GroupedColumnsManagement({
  groups = {},
  searchPlaceholder = "Search",
  ungroupedLabel = "General",
  clientColor = grayColor[2],
}) {
  const apiRef = useGridApiContext();
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);
  const visibility = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const [search, setSearch] = useState("");

  const query = search.trim().toLowerCase();

  const sections = useMemo(() => {
    const list = [];
    const index = {};
    columns.forEach((col) => {
      if (col.hideable === false) return;
      const label = col.headerName || col.field;
      if (query && !label.toLowerCase().includes(query)) return;
      const name = groups[col.field] || ungroupedLabel;
      if (!(name in index)) {
        index[name] = list.length;
        list.push({ name, cols: [] });
      }
      list[index[name]].cols.push(col);
    });
    return list;
  }, [columns, groups, query, ungroupedLabel]);

  const isVisible = (field) => visibility[field] !== false;

  const toggleColumn = (field) =>
    apiRef.current.setColumnVisibility(field, !isVisible(field));

  const toggleGroup = (section) => {
    const allVisible = section.cols.every((c) => isVisible(c.field));
    const model = { ...visibility };
    section.cols.forEach((c) => {
      model[c.field] = !allVisible;
    });
    apiRef.current.setColumnVisibilityModel(model);
  };

  const checkboxSx = {
    p: "4px",
    color: grayColor[6],
    "&.Mui-checked": { color: clientColor },
    [`&.${checkboxClasses.indeterminate}`]: { color: clientColor },
    [`& .${svgIconClasses.root}`]: { fontSize: 16 },
  };

  return (
    <Box
      sx={{
        p: "12px 16px",
        width: 340,
        maxHeight: 420,
        overflowY: "auto",
      }}
    >
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{
          mb: "12px",
          [`& .${outlinedInputClasses.root}`]: { fontSize: "12px" },
          [`& .${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline}`]:
            {
              borderColor: grayColor[10],
            },
          [`& .${outlinedInputClasses.root}.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]:
            { borderColor: clientColor },
        }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: grayColor[4] }} />
              </InputAdornment>
            ),
          },
        }}
      />
      {sections.map((section) => {
        const visibleCount = section.cols.filter((c) =>
          isVisible(c.field),
        ).length;
        const all = visibleCount === section.cols.length;
        const none = visibleCount === 0;
        return (
          <Box
            key={section.name}
            sx={{ mb: "10px", "&:last-child": { mb: 0 } }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: `${clientColor}15`,
                borderRadius: "4px",
                p: "2px 8px 2px 2px",
                mb: "4px",
              }}
            >
              <Checkbox
                size="small"
                checked={all}
                indeterminate={!all && !none}
                onChange={() => toggleGroup(section)}
                sx={checkboxSx}
              />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: clientColor,
                  flex: 1,
                }}
              >
                {section.name}
              </Typography>
              <Typography sx={{ fontSize: "10px", color: grayColor[4] }}>
                {visibleCount}/{section.cols.length}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "2px",
                pl: "16px",
              }}
            >
              {section.cols.map((col) => (
                <Box
                  key={col.field}
                  sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
                >
                  <Checkbox
                    size="small"
                    checked={isVisible(col.field)}
                    onChange={() => toggleColumn(col.field)}
                    sx={checkboxSx}
                  />
                  <Typography
                    noWrap
                    sx={{
                      fontSize: "11px",
                      color: grayColor[2],
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {col.headerName || col.field}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

GroupedColumnsManagement.propTypes = {
  groups: PropTypes.object,
  searchPlaceholder: PropTypes.string,
  ungroupedLabel: PropTypes.string,
  clientColor: PropTypes.string,
};
